import React from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Modal dialog. Built on Radix Dialog primitives for focus trap, aria wiring
 * and Escape handling out of the box.
 *
 * Usage:
 *   <Dialog open={open} onOpenChange={setOpen} title="Confirm" onDark>
 *     Body copy.
 *     <DialogFooter>
 *       <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
 *       <Button onClick={ok}>Confirm</Button>
 *     </DialogFooter>
 *   </Dialog>
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  onDark = false,
  size = "md",
  className,
  hideClose = false,
}) {
  const width = size === "sm" ? "max-w-[360px]" : size === "lg" ? "max-w-[560px]" : "max-w-[420px]";
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className="fixed inset-0 z-[100] bg-[rgba(2,4,11,0.5)] backdrop-blur-[2px] data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          data-testid="dialog-overlay"
        />
        <RadixDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-[101] w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-[16px] p-6 shadow-elev-lg outline-none",
            "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
            onDark
              ? "bg-surface-raised text-ink-primary border border-hairline"
              : "bg-white text-frac-black",
            width,
            className,
          )}
          data-testid="dialog-panel"
        >
          {title && (
            <RadixDialog.Title
              className={cn(
                "text-[19px] font-bold leading-tight",
                onDark ? "text-frac-violet" : "text-frac-purple",
              )}
            >
              {title}
            </RadixDialog.Title>
          )}
          {description && (
            <RadixDialog.Description
              className={cn(
                "mt-2 text-[14.5px] leading-normal",
                onDark ? "text-ink-secondary" : "text-frac-gray-70",
              )}
            >
              {description}
            </RadixDialog.Description>
          )}
          <div className={cn(title || description ? "mt-4" : "")}>{children}</div>

          {!hideClose && (
            <RadixDialog.Close asChild>
              <button
                type="button"
                aria-label="Close dialog"
                data-testid="dialog-close"
                className={cn(
                  "absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors duration-fast ease-out",
                  onDark
                    ? "text-ink-tertiary hover:text-ink-primary hover:bg-white/5"
                    : "text-frac-gray-50 hover:text-frac-purple hover:bg-frac-purple-10",
                )}
              >
                <X className="h-4 w-4" strokeWidth={1.75} />
              </button>
            </RadixDialog.Close>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export function DialogFooter({ children, className }) {
  return (
    <div className={cn("mt-6 flex flex-wrap items-center justify-end gap-3", className)}>
      {children}
    </div>
  );
}

// Passthrough trigger for cases where consumers want to wire their own button.
export const DialogTrigger = RadixDialog.Trigger;

export default Dialog;
